/**
 * De kleuren van de bewerkingskolommen in de houtstaat.
 *
 * Dit zijn de RGB-waarden van de kolomkoppen op het blad `Houtstaat ` in de Excel,
 * zoals De Klerk ze gebruikt. Ze horen bij de **bewerking**, niet bij de afdeling: in
 * de Excel is FINEER 1 net zo groen als FINEER 2, en CNCZ ARDIS B/N net zo geel als
 * CNCZ ARDIS NETTO. Wie de kolom herkent, weet welke machine hem draait.
 *
 * De afdelingskleuren in `afdelingen.ts` blijven bestaan voor de werkvloer en het
 * dashboard: daar gaat het om wáár de kar staat, niet om wélke bewerking de regel
 * vraagt. Twee vragen, twee kleursystemen.
 */
/** De bewerkingen met een eigen kolom in de houtstaat. */
export const BEWERKINGEN = [
    'Fineer',
    'Massief bruto',
    'Winkelen',
    'CNCZ ARDIS B/N',
    'OPDIK',
    'Massief netto',
    'CALI MASSIEF',
    'CALI',
    'Gelijklopen',
    'Pers',
    'Nesting',
    'CNC FR',
    'Bandjes',
    'Algemeen',
    'Schuren',
    'Speciale bewerking',
    'Beitserij',
    'Frezen',
    'Buigen',
];
/** De kleur per bewerking — Excel RGB, vast. */
export const BEWERKINGSKLEUR = {
    Fineer: '#00ff00',
    'Massief bruto': '#3366ff',
    Winkelen: '#ffffff',
    'CNCZ ARDIS B/N': '#ffff00',
    OPDIK: '#008000',
    'Massief netto': '#3366ff',
    'CALI MASSIEF': '#bfbfbf',
    CALI: '#bfbfbf',
    Gelijklopen: '#ccffff',
    Pers: '#339966',
    Nesting: '#ff6600',
    'CNC FR': '#ff6600',
    Bandjes: '#ccffff',
    Algemeen: '#ccffff',
    Schuren: '#bfbfbf',
    'Speciale bewerking': '#ff8080',
    Beitserij: '#993300',
    Frezen: '#ffcc99',
    Buigen: '#000000',
};
/**
 * Leesbare tekstkleur op een achtergrond.
 *
 * Relatieve helderheid volgens WCAG. Nodig omdat de Excel-kleuren van wit
 * (`Winkelen`) tot zwart (`Buigen`) lopen: één vaste tekstkleur zou de helft van de
 * kolommen onleesbaar maken.
 */
export function tekstOpKleur(achtergrond) {
    const hex = achtergrond.replace('#', '');
    if (hex.length !== 6)
        return '#ffffff';
    const kanaal = (deel) => {
        const v = parseInt(deel, 16) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    const helderheid = 0.2126 * kanaal(hex.slice(0, 2)) +
        0.7152 * kanaal(hex.slice(2, 4)) +
        0.0722 * kanaal(hex.slice(4, 6));
    return helderheid > 0.45 ? '#1a1a1a' : '#ffffff';
}
export const BEWERKINGSTEKST = Object.fromEntries(BEWERKINGEN.map((b) => [b, tekstOpKleur(BEWERKINGSKLEUR[b])]));
/**
 * De bewerking achter een kolomnaam uit een bewerkingsregel.
 *
 * De regels heten `FINEER 1`, `FINEER 2`, `CNCZ NETTO`, `SCHUURMACHINE`; de kleurtabel
 * kent `Fineer`, `CNCZ ARDIS B/N`, `CALI`. Deze tabel legt die twee naast elkaar, zodat
 * een nieuwe regel alleen een kolomnaam hoeft te kiezen die hier voorkomt.
 *
 * `CONTROLE` en `GEREED` staan er bewust niet in: dat zijn geen bewerkingen uit de
 * houtstaat maar stappen in de doorloop. Die houden hun afdelingskleur.
 */
const KOLOM_NAAR_BEWERKING = [
    [/^fineer/, 'Fineer'],
    [/^massief\s*bruto/, 'Massief bruto'],
    [/^massief\s*netto/, 'Massief netto'],
    [/^winkel/, 'Winkelen'],
    [/^cncz/, 'CNCZ ARDIS B/N'],
    [/^opdik/, 'OPDIK'],
    [/^cali\s*massief/, 'CALI MASSIEF'],
    [/^cali/, 'CALI'],
    [/^schuur/, 'Schuren'],
    [/^gelijklopen/, 'Gelijklopen'],
    [/^pers/, 'Pers'],
    [/^nesting/, 'Nesting'],
    [/^cnc\s*fr/, 'CNC FR'],
    [/^bandje/, 'Bandjes'],
    [/^algemeen/, 'Algemeen'],
    [/^speciale/, 'Speciale bewerking'],
    [/^beits/, 'Beitserij'],
    [/^frezen/, 'Frezen'],
    [/^buigen/, 'Buigen'],
];
export function bewerkingVan(kolomnaam) {
    const schoon = kolomnaam.trim().toLowerCase();
    for (const [patroon, bewerking] of KOLOM_NAAR_BEWERKING) {
        if (patroon.test(schoon))
            return bewerking;
    }
    return null;
}
/** De kleur van een kolom, of `null` als het geen houtstaatbewerking is. */
export function kolomkleur(kolomnaam) {
    const bewerking = bewerkingVan(kolomnaam);
    if (!bewerking)
        return null;
    return { kleur: BEWERKINGSKLEUR[bewerking], tekst: BEWERKINGSTEKST[bewerking] };
}
//# sourceMappingURL=bewerkingskleuren.js.map